import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Ship, Mountain, Castle, Palmtree, Train, Plane, Compass, Heart } from 'lucide-react';

const ExperiencesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const experiences = [
    {
      title: 'Private Yacht Charters',
      description: 'Navigate the world\'s most exclusive waters aboard luxury yachts with dedicated crew. From the Mediterranean to the Caribbean, experience maritime luxury at its finest.',
      icon: Ship,
      image: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?q=80&w=2574&auto=format&fit=crop',
    },
    {
      title: 'Luxury Safari Journeys',
      description: 'Witness Africa\'s magnificent wildlife from intimate luxury camps. Private game drives, expert guides, and unparalleled comfort in the heart of the wilderness.',
      icon: Compass,
      image: 'https://images.unsplash.com/photo-1709402606682-400133d92ab2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0cmF2ZWwlMjBzYWZhcmklMjBzZXJlbmdldGklMjBzdW5zZXQlMjBwcml2YXRlJTIwamVlcHxlbnwwfHx8fDE3NzMxNDQ1NTN8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Palace Stays in India',
      description: 'Experience royal heritage with stays in India\'s finest palace hotels. From Rajasthan\'s majestic forts to Kerala\'s serene backwaters, immerse in regal luxury.',
      icon: Castle,
      image: 'https://images.unsplash.com/photo-1770665567877-72ee8a7c9051?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwbGFrZSUyMHBhbGFjZSUyMGx1eHVyeSUyMGhvdGVsJTIwaW5kaWF8ZW58MHx8fHwxNzczMTQ0NTU0fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Private Island Escapes',
      description: 'Own your paradise with exclusive private island rentals. Complete privacy, personalized service, and untouched natural beauty in destinations like Maldives and Seychelles.',
      icon: Palmtree,
      image: 'https://images.unsplash.com/photo-1620483829646-8fb1d8162108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtYWxkaXZlcyUyMGx1eHVyeSUyMHdhdGVyJTIwdmlsbGElMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3NzMxNDQ1NTR8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Luxury Train Journeys',
      description: 'Travel through time aboard the world\'s most luxurious trains. From the Orient Express to Maharajas\' Express, journey in unparalleled style and comfort.',
      icon: Train,
      image: 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?q=80&w=2574&auto=format&fit=crop',
    },
    {
      title: 'Helicopter Expeditions',
      description: 'Soar above breathtaking landscapes with private helicopter tours. Access remote locations and experience aerial perspectives of the world\'s wonders.',
      icon: Plane,
      image: 'https://images.unsplash.com/photo-1581701391351-c8bc1703f456?q=80&w=2532&auto=format&fit=crop',
    },
    {
      title: 'Cultural Immersion',
      description: 'Deep, authentic cultural experiences curated by local experts. Private museum tours, master classes with artisans, and exclusive access to heritage sites.',
      icon: Mountain,
      image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2670&auto=format&fit=crop',
    },
    {
      title: 'Ultra Luxury Honeymoons',
      description: 'Celebrate your love with bespoke romantic journeys. From Maldives overwater villas to Santorini sunsets, create memories that last forever.',
      icon: Heart,
      image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=2670&auto=format&fit=crop',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="experiences-page"
    >
      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-center justify-center overflow-hidden" data-testid="experiences-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?q=80&w=2532&auto=format&fit=crop"
            alt="Luxury Experience"
            className="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Signature
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary mb-6"
            data-testid="experiences-title"
          >
            Curated
            <br />
            Experiences
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto"
          >
            Extraordinary moments crafted for the extraordinary traveler
          </motion.p>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="section-padding container-padding" data-testid="experiences-grid-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="space-y-20">
            {experiences.map((experience, index) => {
              const Icon = experience.icon;
              const isEven = index % 2 === 0;

              return (
                <motion.div
                  key={experience.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                    !isEven ? 'lg:direction-rtl' : ''
                  }`}
                  data-testid={`experience-${index}`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={experience.image}
                        alt={experience.title}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <Icon className="text-accent mb-6" size={48} />
                    <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
                      {experience.title}
                    </h2>
                    <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                      {experience.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ExperiencesPage;